/**
 * GlobalChatSystem Utilities
 *
 * This file contains helper functions used throughout the global chat system.
 * These utilities handle HTML sanitization, text conversion, file size formatting, and more.
 */

import { quotaSafeSetItem } from './storageQuota';
import { flushDeferredThreadSave, readHeldCopy, releaseHeldCopy } from './threadCacheTier';
import { healUserBubbleHtml } from './pasteSanitizer';
import { isComposerChip, chipTextInContext } from '../../services/threadMentions/composerChip';

// ============================================================================
// HTML Sanitization
// ============================================================================

/**
 * Allowed HTML tags for rich text content.
 * Expanded to support professional document formatting (headings, tables, etc.)
 */
const ALLOWED_TAGS = new Set([
  // Inline formatting
  'a', 'b', 'strong', 'i', 'em', 'u', 'span', 'br', 'sub', 'sup', 'mark', 'small', 'cite', 'abbr', 's', 'del', 'ins',
  // Block elements
  'p', 'div', 'blockquote', 'pre', 'code', 'hr',
  // Headings
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  // Lists
  'ul', 'ol', 'li',
  // Interactive elements (for in-chat action buttons)
  'button',
  // Tables
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  // Semantic sections (for professional reports)
  'section', 'article', 'header', 'footer', 'aside', 'main', 'nav', 'figure', 'figcaption', 'time',
]);

/**
 * Allowed CSS style properties.
 * Expanded to support professional document styling and pending widget animations.
 */
const ALLOWED_STYLES = new Set([
  // Typography
  'font-weight', 'font-style', 'font-size', 'font-family', 'text-decoration', 'text-align', 'text-transform', 'letter-spacing', 'line-height', 'white-space', 'word-break', 'word-wrap', 'overflow-wrap',
  // Colors
  'color', 'background-color', 'background', 'opacity',
  // Spacing
  'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  // Borders
  'border', 'border-top', 'border-right', 'border-bottom', 'border-left',
  'border-width', 'border-style', 'border-color', 'border-radius', 'border-collapse', 'border-spacing',
  'border-top-color', // For spinner top border
  // Layout
  'display', 'flex-direction', 'flex-wrap', 'justify-content', 'align-items', 'gap', 'vertical-align',
  'flex-shrink', 'flex-grow', 'flex-basis', 'flex',
  // Size
  'width', 'max-width', 'min-width', 'height', 'max-height', 'min-height',
  // List
  'list-style', 'list-style-type', 'list-style-position',
  // Animations (for pending widget spinner/dots)
  'animation', 'animation-name', 'animation-duration', 'animation-timing-function', 'animation-delay', 'animation-iteration-count', 'animation-fill-mode',
  // Effects (for pending widget glow/shimmer)
  'box-shadow', 'background-size', 'background-position', 'transform',
  // Interaction
  'user-select', 'cursor', 'pointer-events',
]);

// ============================================================================
// Internal (same-origin) link policy — L3, 2026-07-15
// ============================================================================

/**
 * Same-origin RELATIVE anchor hrefs allowed to SURVIVE sanitization so guest
 * notices / sign-in invitations can carry a real clickable route (e.g.
 * `/auth`). Deliberately conservative:
 *   - must start with a SINGLE '/' (protocol-relative `//evil.com` rejected);
 *   - path segments of [a-z0-9-] only — no ':', so no scheme smuggling;
 *   - no query string, no hash, no encoded characters, no backslashes.
 * Everything that fails BOTH this and the http(s) check keeps the existing
 * unwrap behavior (javascript:/data:/vbscript:/mailto:/relative-without-slash
 * all stay stripped).
 */
export const INTERNAL_ANCHOR_HREF_RE = /^\/(?!\/)[a-z0-9-]+(?:\/[a-z0-9-]+)*\/?$/i;

/**
 * The ONE parameterized internal anchor shape allowed beyond the bare-path
 * form above (2026-08-14; `setup` added 2026-08-24): the in-chat
 * "manage access & folders" and "finish setup" deep links. Route fixed to
 * /integrations, a single `manage` OR `setup` param, slug charset
 * [a-z0-9_-] only, bounded length — nothing else with a query string
 * survives. Since 2026-09-29 (legal apps 2a) also exactly `category=legal`,
 * the page's legal apps view; no other category value.
 */
export const INTERNAL_MANAGE_ANCHOR_HREF_RE = /^\/integrations\?(?:(?:manage|setup)=[a-z0-9_-]{1,64}|category=legal)$/i;

/**
 * The SECOND parameterized internal anchor shape (2026-09-20, P6b of the
 * connection-experience repair): "show me on the desktop page" — a computer
 * run's card links to the desktop session the run actually drove. Route fixed
 * to /desktop, a `session` id in the browser-service's own id charset, and an
 * OPTIONAL `svc` naming the member workstation the run used — the value is
 * the URL-encoded https origin of one of OUR dashed-sslip hosts, optionally
 * with its /browser-api path, the exact rule AgentActivity enforces on
 * arrival. Nothing else with a query string survives; a second parameter, a
 * foreign host or a scheme in the id all fail the shape and unwrap to text.
 */
export const INTERNAL_DESKTOP_ANCHOR_HREF_RE =
  /^\/desktop\?session=desktop-[a-z0-9-]{6,64}(?:&svc=https%3A%2F%2F(?:\d{1,3}-){3}\d{1,3}\.sslip\.io(?:%2Fbrowser-api)?)?$/i;

/**
 * Canonical PUBLIC site routes (F3/F6, adversarial review 2026-07-15) — the
 * ONE source of truth for both:
 *   (a) autolinking in chat bubbles (plain-text route mentions and markdown
 *       [label](/route) links in guest replies), and
 *   (b) the sanitizer's internal-anchor whitelist (which internal <a href>s
 *       survive sanitizeHtmlInput).
 * Mirrors the indexed public route list in
 * functions/src/guestSession/knowledgeSource.generated.json plus the /trial
 * and /signup app aliases. The private tenant surfaces (/justiceisurright,
 * /citadel) are deliberately absent and must never be added.
 */
export const PUBLIC_SITE_ROUTES: readonly string[] = [
  '/',
  '/about',
  '/security',
  '/team',
  '/mission',
  '/insights',
  '/pricing',
  '/start',
  '/trial',
  '/signup',
  '/for-lawyers',
  '/for-individuals',
  '/for-enterprise',
  '/legal',
  '/privacy-policy',
  '/terms-of-service',
  '/cookie-policy',
  // The Trust Center (2026-09-11). A visitor doing vendor diligence is exactly
  // who asks the signed-out Prest0n "who processes my data" — being told the
  // page exists and not being able to click it is the wrong half of an answer.
  '/trust',
  // The open ROI reports, their calculator and the solutions pages (GSD revamp, 2026-10-15; the list is
  // src/config/gsdPublicPages.ts): a visitor who asks what Prest0n would return to their firm can open the report.
  '/roi',
  '/roi/law',
  '/roi/finance',
  '/roi/marketing',
  '/roi/general',
  '/roi/methodology',
  '/roi/data',
  '/roi/calculator',
  '/solutions/law',
  '/solutions/finance',
  '/solutions/professional',
  // The booking calendar as a page (2026-10-13): where "can I see a demo?" is answered with one click.
  '/demo',
  // The referral program's public page (2026-10-19, docs/PLAN-referral-program-20261019.md): "how do I earn by
  // referring someone?" is answered with the page that sets every expectation.
  '/referrals',
  // Its terms on their own page: "what are the rules?" is answered with the terms themselves.
  '/referral-terms',
  // What's new (2026-10-21): every update to prest0, month by month, the ROI reports' versions among them (their own
  // changelog, /roi/changelog, folded into it). "What changed lately?" is answered with the page.
  '/changelog',
];

/**
 * App-shell routes chat copy legitimately links besides the public pages:
 * /auth (the L3 sign-in invitation), /desktop (the member workspace), and
 * /integrations (composio connect notices in member chat). Anchor-whitelist
 * only — these are NOT autolinked from plain text.
 */
const INTERNAL_APP_ANCHOR_ROUTES: readonly string[] = ['/auth', '/desktop', '/integrations'];

/**
 * Signed-in member pages Prest0n links when guiding someone through the
 * platform (2026-09-03, pricing-transcript incident: replies said "head to
 * the Subscription page" with no link because /subscription was not on the
 * whitelist — a markdown [Subscription](/subscription) unwrapped to plain
 * text). Every route here is behind <ProtectedRoute> in src/App.tsx (a signed-
 * out click lands on sign-in, never on data); the private tenant surfaces
 * stay absent, as above. Anchor-whitelisted AND autolinked from plain text.
 */
export const INTERNAL_MEMBER_ROUTES: readonly string[] = [
  '/wallet',
  '/subscription',
  '/settings',
  '/settings/team',
  '/settings/skills',
  '/activity',
  '/notifications',
  '/profile',
  '/organization',
  '/portal-controls',
  '/onboarding',
  '/infrastructure',
];

/**
 * Whitelisted internal routes that `autoLinkText` converts from plain text
 * into same-origin anchors when they appear in chat bubbles (guest replies,
 * quota notices, the sign-in invitation): /auth + every canonical public
 * route (F3). '/' is excluded — a bare slash in prose is punctuation, not a
 * route mention (and INTERNAL_ANCHOR_HREF_RE rejects it anyway).
 */
export const INTERNAL_AUTOLINK_ROUTES: readonly string[] = [
  '/auth',
  ...PUBLIC_SITE_ROUTES.filter((route) => route !== '/'),
  // Member pages (2026-09-03): a plain "/subscription" in a signed-in reply is
  // a page mention and becomes a link like the public routes do.
  '/desktop',
  '/integrations',
  ...INTERNAL_MEMBER_ROUTES,
];

/**
 * F6: internal anchors that survive `sanitizeHtmlInput` — the canonical
 * public routes plus the app-shell chat-link routes above. Everything else
 * internal unwraps to text even when it matches INTERNAL_ANCHOR_HREF_RE.
 */
const INTERNAL_ANCHOR_ROUTE_WHITELIST: ReadonlySet<string> = new Set([
  ...INTERNAL_APP_ANCHOR_ROUTES,
  ...INTERNAL_MEMBER_ROUTES,
  ...PUBLIC_SITE_ROUTES,
]);

/**
 * Parameterized internal routes that survive the sanitizer as PREFIXES: the id
 * segment after the prefix must be `[a-z0-9-]` (already guaranteed upstream by
 * INTERNAL_ANCHOR_HREF_RE). Currently: the tier-21 interactive 3D preview viewer
 * (2026-08-01) — artifact ids are sanitizer-safe by construction (`t3d-...`).
 */
const INTERNAL_ANCHOR_ROUTE_PREFIXES: readonly string[] = ['/preview/3d/'];

/**
 * Whitelist membership for an internal href (case-insensitive, tolerant of a
 * single trailing slash — mirrors what INTERNAL_ANCHOR_HREF_RE accepts).
 */
export function isWhitelistedInternalRoute(href: string): boolean {
  const trimmed = String(href || '').trim().toLowerCase();
  const normalized = trimmed.length > 1 ? trimmed.replace(/\/+$/, '') : trimmed;
  if (INTERNAL_ANCHOR_ROUTE_PREFIXES.some((p) => normalized.startsWith(p) && normalized.length > p.length)) {
    return true;
  }
  return INTERNAL_ANCHOR_ROUTE_WHITELIST.has(normalized);
}

/**
 * Should a click on a chat-bubble anchor be routed through React Router
 * instead of a full page load? (2026-09-03) Internal links used to render
 * same-tab but trigger a full SPA reboot — jarring, and it dropped the open
 * chat. TRUE only for a plain left-click (no modifier keys, so open-in-new-
 * tab gestures keep working) on a whitelisted internal href (bare-path
 * whitelist or the one parameterized /integrations?manage|setup shape) that
 * does not ask for a new tab. Pure — GlobalChatSystem's delegated click
 * handler consults it and calls navigate(href) when it says yes.
 */
export function shouldNavigateInApp(
  href: string | null | undefined,
  click: { button?: number; metaKey?: boolean; ctrlKey?: boolean; shiftKey?: boolean; altKey?: boolean },
  anchorTarget?: string | null,
): boolean {
  const h = String(href || '').trim();
  if (!h) return false;
  if ((click.button ?? 0) !== 0) return false;
  if (click.metaKey || click.ctrlKey || click.shiftKey || click.altKey) return false;
  if (anchorTarget && anchorTarget.toLowerCase() === '_blank') return false;
  if (INTERNAL_MANAGE_ANCHOR_HREF_RE.test(h)) return true;
  if (INTERNAL_DESKTOP_ANCHOR_HREF_RE.test(h)) return true;
  return INTERNAL_ANCHOR_HREF_RE.test(h) && isWhitelistedInternalRoute(h);
}

/**
 * Filter CSS style string to only include allowed properties.
 */
function filterStyle(styleValue: string): string {
  const parts = styleValue.split(';').map(s => s.trim()).filter(Boolean);
  const kept: string[] = [];
  for (const part of parts) {
    const [propRaw, ...valParts] = part.split(':');
    if (!propRaw || valParts.length === 0) continue;
    const prop = propRaw.trim().toLowerCase();
    const val = valParts.join(':').trim();
    if (ALLOWED_STYLES.has(prop)) {
      kept.push(`${prop}: ${val}`);
    }
  }
  return kept.join('; ');
}

/**
 * Sanitize HTML input to only allow safe tags and styles.
 * This prevents XSS attacks while preserving basic formatting.
 * 
 * @param html - The raw HTML string to sanitize
 * @returns Sanitized HTML string with only allowed tags and styles
 */
export function sanitizeHtmlInput(html: string): string {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // Unwrap an element in place: its children land exactly where it stood.
    // Returns the first of them so the walk continues THROUGH them — a
    // snapshot loop (`Array.from(childNodes)`) skipped the moved children, so
    // `<custom><img onerror></custom>` came out with the img and its handler
    // intact (security remediation slice 4, 2026-09-15; pinned by
    // __tests__/htmlSinkSanitizers.test.ts).
    const unwrap = (parent: Node, el: HTMLElement): Node | null => {
      const first = el.firstChild;
      while (el.firstChild) {
        parent.insertBefore(el.firstChild, el);
      }
      parent.removeChild(el);
      return first;
    };
    const strip = (node: Node): void => {
      let child: Node | null = node.firstChild;
      while (child) {
        const next: Node | null = child.nextSibling;
        if (child.nodeType === Node.ELEMENT_NODE) {
          const el = child as HTMLElement;
          const tagName = el.tagName.toLowerCase();
          if (!ALLOWED_TAGS.has(tagName)) {
            // Unwrap disallowed tags by replacing with children
            child = unwrap(node, el) ?? next;
            continue;
          } else {
            // Keep allowed tags but sanitize attributes
            const attrNames = el.getAttributeNames();

            // Special handling for anchors: allow safe http(s) href (new-tab +
            // noopener/noreferrer enforced) and — L3 (2026-07-15) — same-origin
            // RELATIVE hrefs matching INTERNAL_ANCHOR_HREF_RE, which navigate
            // in the SAME tab (target/rel stripped). F6 (adversarial review
            // 2026-07-15): a clean SHAPE is no longer enough — the internal
            // href must also be on the canonical route whitelist
            // (PUBLIC_SITE_ROUTES + /auth /desktop /integrations); any other
            // internal path unwraps to text. Everything else unwraps too.
            if (tagName === 'a') {
              const hrefRaw = el.getAttribute('href') || '';
              const href = hrefRaw.trim();
              const isHttp = /^https?:\/\//i.test(href);
              const isInternal =
                !isHttp &&
                ((INTERNAL_ANCHOR_HREF_RE.test(href) && isWhitelistedInternalRoute(href)) ||
                  INTERNAL_MANAGE_ANCHOR_HREF_RE.test(href) ||
                  INTERNAL_DESKTOP_ANCHOR_HREF_RE.test(href));
              if (!isHttp && !isInternal) {
                // unwrap if href is missing or unsafe
                child = unwrap(node, el) ?? next;
                continue;
              }

              const allowedAttrs = new Set(['href', 'target', 'rel', 'style', 'class']);
              for (const name of attrNames) {
                if (!allowedAttrs.has(name)) {
                  el.removeAttribute(name);
                } else if (name === 'style') {
                  const filtered = filterStyle(el.getAttribute('style') || '');
                  if (filtered) {
                    el.setAttribute('style', filtered);
                  } else {
                    el.removeAttribute('style');
                  }
                }
              }

              el.setAttribute('href', href);
              if (isHttp) {
                el.setAttribute('target', '_blank');
                const relSet = new Set((el.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
                relSet.add('noopener');
                relSet.add('noreferrer');
                el.setAttribute('rel', Array.from(relSet).join(' '));
              } else {
                // Same-origin route: keep it a plain same-tab navigation (a
                // crafted target="_blank" is stripped; rel is meaningless
                // same-origin).
                el.removeAttribute('target');
                el.removeAttribute('rel');
              }
            } else if (tagName === 'button') {
              // Button: allow id, style, class for interactive elements
              const allowedBtnAttrs = new Set(['id', 'style', 'class', 'type', 'disabled']);
              for (const name of attrNames) {
                if (!allowedBtnAttrs.has(name)) {
                  el.removeAttribute(name);
                } else if (name === 'style') {
                  const filtered = filterStyle(el.getAttribute('style') || '');
                  if (filtered) {
                    el.setAttribute('style', filtered);
                  } else {
                    el.removeAttribute('style');
                  }
                }
              }
            } else {
              // Non-anchor, non-button: keep only style/class — plus a numbered
              // list's `start`, digits only (2026-09-22). A numbered list broken
              // by a paragraph resumes at its own number ("3.") instead of
              // restarting at 1; nothing else about the attribute can carry
              // behaviour, so a non-numeric value is dropped like any other.
              for (const name of attrNames) {
                if (tagName === 'ol' && name === 'start') {
                  if (!/^\d{1,6}$/.test(el.getAttribute('start') || '')) el.removeAttribute('start');
                  continue;
                }
                if (name !== 'style' && name !== 'class') {
                  el.removeAttribute(name);
                }
                if (name === 'style') {
                  const filtered = filterStyle(el.getAttribute('style') || '');
                  if (filtered) {
                    el.setAttribute('style', filtered);
                  } else {
                    el.removeAttribute('style');
                  }
                }
              }
              // A COMPOSER MENTION CHIP IS ATOMIC, and this is what keeps it
              // that way (2026-09-22). The loop above has just removed every
              // attribute but style and class — `contenteditable` included —
              // and this function runs on EVERY input event, so an attribute
              // set once at insertion would not survive a single keystroke.
              // Re-deriving it from the class instead makes it self-healing:
              // however the chip arrived, it is atomic again by the next
              // event, and Backspace deletes the person rather than walking
              // back through "Malaik", "Malai", "Mala".
              //
              // A PASTED span cannot acquire it: `sanitizePastedHtml` strips
              // every class unconditionally, so pasted markup is never a chip
              // by the time this can see it. That asymmetry — class survives
              // typing, class never survives paste — is the whole reason the
              // marker is a class.
              if (isComposerChip(el)) el.setAttribute('contenteditable', 'false');
            }

            strip(el);
          }
        } else if (child.nodeType === Node.TEXT_NODE) {
          // Keep text nodes as-is
        } else {
          // Remove other node types (comments, processing instructions, etc.)
          node.removeChild(child);
        }
        child = next;
      }
    };

    strip(doc.body);
    return doc.body.innerHTML;
  } catch {
    // Fallback: strip all HTML tags
    return html.replace(/<\/?[^>]+(>|$)/g, '');
  }
}

/**
 * Trim whitespace-only nodes and stray breaks at the edges of chat HTML.
 * Ensures submitted chat messages do not render with empty lines/padding.
 *
 * IMPORTANT: This function is intentionally conservative to avoid trimming
 * valid content. It only removes:
 * - Leading/trailing whitespace-only text nodes
 * - Leading/trailing <br> elements
 * - Consecutive <br> tags throughout the content
 *
 * It does NOT remove block elements (like <p>, <div>) even if they appear empty,
 * as these may contain important structure for research reports and references.
 */
export function trimChatHtmlWhitespace(
  html: string,
  opts?: {
    /**
     * Keep consecutive <br> runs. USER-authored html carries the author's
     * deliberate blank lines (owner report 2026-09-07); only assistant html
     * gets the "excessive spacing" sweep.
     */
    preserveBlankLines?: boolean;
  },
): string {
  if (!html) return '';

  try {
    const doc = new DOMParser().parseFromString(`<div data-trim-root>${html}</div>`, 'text/html');
    const root = doc.querySelector('div[data-trim-root]');
    if (!root) return html.trim();

    // Conservative check: only trim text nodes and <br> elements at edges
    // Do NOT trim block elements even if they appear empty (may contain references, etc.)
    const isTrimmableEdgeNode = (node: Node): boolean => {
      // Whitespace-only text nodes can be trimmed
      if (node.nodeType === Node.TEXT_NODE) {
        return !(node.textContent || '').trim();
      }
      // Non-element nodes (comments, etc.) can be trimmed
      if (node.nodeType !== Node.ELEMENT_NODE) return true;
      const el = node as HTMLElement;
      // Only <br> elements can be trimmed - NOT empty block elements
      // This prevents accidentally removing references sections that may appear empty
      return el.tagName === 'BR';
    };

    while (root.firstChild && isTrimmableEdgeNode(root.firstChild)) {
      root.removeChild(root.firstChild);
    }

    while (root.lastChild && isTrimmableEdgeNode(root.lastChild)) {
      root.removeChild(root.lastChild);
    }

    // Remove consecutive <br/> tags throughout ASSISTANT content. A user's
    // own <br><br> is a typed blank line and is kept (preserveBlankLines).
    if (!opts?.preserveBlankLines) {
      const brElements = Array.from(root.querySelectorAll('br'));
      for (const br of brElements) {
        // Check if next sibling is also a <br/>
        let nextSibling = br.nextSibling;
        while (nextSibling && nextSibling.nodeName === 'BR') {
          const toRemove = nextSibling;
          nextSibling = nextSibling.nextSibling;
          toRemove.parentNode?.removeChild(toRemove);
        }
      }
    }

    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    let current = walker.nextNode();
    while (current) {
      textNodes.push(current as Text);
      current = walker.nextNode();
    }

    if (textNodes.length > 0) {
      const first = textNodes[0];
      if (first?.textContent) {
        first.textContent = first.textContent.replace(/^\s+/, '');
      }
      const last = textNodes[textNodes.length - 1];
      if (last?.textContent) {
        last.textContent = textNodes.length === 1
          ? last.textContent.trim()
          : last.textContent.replace(/\s+$/, '');
      }
    }

    return root.innerHTML;
  } catch {
    return html.trim();
  }
}

/**
 * Every chat <table> rides inside a horizontal-scroll wrapper (2026-08-02 Miles
 * report: at docked widths a bare table crushed to one-letter-per-line columns —
 * the bubble's word-break/pre-wrap rules are exactly wrong inside table cells).
 * The wrapper + the Message/tile CSS (chat-table-scroll / table rules) keep rows
 * and columns readable at EVERY pane width; narrow panes scroll the table
 * sideways instead of shredding it. Idempotent — already-wrapped tables pass
 * through untouched.
 */
export function wrapTablesForScroll(html: string): string {
  if (!html || !/<table\b/i.test(html)) return html;
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.body.querySelectorAll('table').forEach((t) => {
      const parent = t.parentElement;
      if (parent && parent.classList.contains('chat-table-scroll')) return;
      const wrap = doc.createElement('div');
      wrap.className = 'chat-table-scroll';
      t.replaceWith(wrap);
      wrap.appendChild(t);
    });
    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

/**
 * Sanitize HTML and remove leading/trailing whitespace before storing or rendering.
 */
// ── Retired developer-notice heal (2026-08-28 owner report) ─────────────────
//
// A server-side capability notice used to be appended to the reply body —
// and on surface-owned turns (deep research card, legal doc) the body is
// blank BY DESIGN, so the notice shipped as an ENTIRE assistant message:
// "A packaged slide file is not available on this lane." landed twice as a
// standalone bubble in a thread about verifying client websites. The server
// bug is fixed, but the messages are STORED — in per-user and shared group
// stores the viewer may not even own — so, exactly like the stored-bubble
// html heal above, they are suppressed at render rather than rewritten.
//
// Deliberately narrow: ONLY an assistant message whose ENTIRE body is one of
// the retired notice lines (optionally followed by the old substitute tail)
// is hidden. A notice appended under a real answer stays — it is dated
// wording under real content, not a phantom message.
const RETIRED_DEV_NOTICE_RES: readonly RegExp[] = [
  /^[A-Za-z][^\n]{0,120} is not available on this lane\.(?:\s+Attached[^\n]{0,160})?$/,
  /^A photographic render is not something this account can generate yet\.(?:\s+Attached[^\n]{0,160})?$/,
];

/** True when a STORED assistant message is nothing but a retired dev notice. */
export function isRetiredDevNoticeMessage(message: {
  isUser?: boolean;
  text?: string;
  attachments?: unknown[];
}): boolean {
  if (message.isUser) return false;
  if (Array.isArray(message.attachments) && message.attachments.length > 0) return false;
  const body = String(message.text || '').trim();
  if (!body || body.length > 320) return false;
  return RETIRED_DEV_NOTICE_RES.some((re) => re.test(body));
}

export function normalizeChatHtml(
  html: string,
  opts?: {
    /**
     * Present ⇒ this is a USER bubble: run the render-time heal for stored
     * pre-fix pasted markup (see healUserBubbleHtml) against the bubble's
     * actual ground color. Assistant html never passes this — its classes and
     * widget styles are legitimate.
     */
    userBubble?: { background: string };
    /**
     * Keep the author's consecutive <br> runs (a typed blank line). Defaults
     * to true for USER bubbles (`userBubble` present) and false otherwise —
     * assistant html keeps today's "excessive spacing" sweep.
     */
    preserveBlankLines?: boolean;
  },
): string {
  if (!html) return '';
  const sanitized = sanitizeHtmlInput(html);
  const healed = opts?.userBubble
    ? healUserBubbleHtml(sanitized, { background: opts.userBubble.background })
    : sanitized;
  const preserveBlankLines = opts?.preserveBlankLines ?? Boolean(opts?.userBubble);
  return wrapTablesForScroll(trimChatHtmlWhitespace(healed, { preserveBlankLines }));
}

/**
 * Ensure all EXTERNAL anchor tags open in a new tab with noopener/noreferrer
 * for safety. Same-origin relative anchors (INTERNAL_ANCHOR_HREF_RE — e.g.
 * the `/auth` sign-in invitation) are left as plain same-tab navigations.
 */
export function ensureLinksOpenInNewTab(html: string): string {
  if (typeof window === 'undefined') return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('a[href]').forEach((a) => {
      const href = (a.getAttribute('href') || '').trim();
      if (INTERNAL_ANCHOR_HREF_RE.test(href)) return; // internal route: same tab
      // The manage/setup deep links are internal too (2026-08-24 fix: they
      // used to fail this exemption on their query string and got _blank
      // re-added — a full second tab for an in-app destination).
      if (INTERNAL_MANAGE_ANCHOR_HREF_RE.test(href)) return;
      // The desktop-run card's link is internal too (2026-09-20, P6b): the
      // same defect the manage/setup pills paid for — a query string on an
      // in-app destination must never earn a second tab.
      if (INTERNAL_DESKTOP_ANCHOR_HREF_RE.test(href)) return;
      a.setAttribute('target', '_blank');
      const rel = (a.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
      if (!rel.includes('noopener')) rel.push('noopener');
      if (!rel.includes('noreferrer')) rel.push('noreferrer');
      a.setAttribute('rel', rel.join(' ').trim());
    });
    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

// ============================================================================
// URL-safe link tokenization (link-integrity fix, 2026-08-11)
// ============================================================================
//
// Product-owner screenshots (2026-08-11): a Google OAuth URL rendered with the
// anchor covering only ".../oauth2/auth?access" — the underscore pairs inside
// the raw query string were eaten as emphasis by the inline-formatting passes
// BEFORE the autolinker ran, splitting the URL. Companion defects: a trailing
// comma riding into the href, and a URL broken at a formatting boundary.
//
// Fix: BOTH converters (autoLinkText below and markdownToHtml in
// src/utils/markdownProcessor.ts) stash (i) inline code spans, (ii) markdown
// [text](url) links, and (iii) bare URLs behind opaque placeholder tokens
// BEFORE any formatting pass, and restore them at the end as finished anchors.
// The token alphabet (private-use sentinels around "LNK<n>") is matched by NO
// other regex in either pipeline, so underscores / asterisks / tildes inside
// a URL can never be re-interpreted as formatting.

/** Opaque placeholder for a stashed link/code span: \uE000LNK<n>\uE001 —
 * Unicode private-use sentinels bracket the id so ordinary prose (even the
 * literal text "LNK0") can never collide with a token. */
export const linkToken = (i: number): string => `\uE000LNK${i}\uE001`;

/** Matches placeholders minted by linkToken. Case-insensitive because header
 *  normalization in markdownProcessor may lowercase an ALL-CAPS line that
 *  contains a token. Global — use only with .replace(). */
export const LINK_TOKEN_RE = /\uE000LNK(\d+)\uE001/gi;

/**
 * Bare-URL matcher: http(s):// or www. URLs.
 *  - The character class keeps `_ % & = + # ~ '` and friends inside the match
 *    (the OAuth defect), excluding only whitespace, angle brackets,
 *    double-quote, backtick, and the placeholder sentinels.
 *  - Lookbehinds: never start mid-word / after `@` (emails) / after `.` or `/`
 *    (inside a larger URL), and never inside an `="…"` / `='…'` attribute
 *    value of pre-existing markup.
 *  - Trailing sentence punctuation is NOT the regex's job — callers must trim
 *    with splitTrailingPunctuation so `https://x.com,` keeps the comma out of
 *    the href.
 * Global regex — use only with .replace() (which resets lastIndex per call).
 */
// Square brackets are excluded (GFM-style): a `[2]` citation directly after a
// URL must stay a citation, not be swallowed into the href (review F8).
export const BARE_URL_RE = /(?<![\w@./])(?<!=")(?<!=')(?:https?:\/\/|www\.)[^\s<>"`[\]\uE000\uE001]+/gi;

/** Trailing characters that are overwhelmingly sentence punctuation, plus the
 *  emphasis marker `*` (so `**https://x.com/a_b**` keeps its closing bold
 *  intact). `_` and `~` are deliberately NOT trimmed — they are legitimate URL
 *  tail characters and the autolinker must never terminate at them. */
const TRAILING_URL_PUNCT = new Set([',', '.', ';', ':', '!', '?', "'", '"', '…', '*']);
const URL_CLOSER_TO_OPENER: Record<string, string> = { ')': '(', ']': '[', '}': '{' };

/**
 * Standard autolinker trailing-punctuation trimming: peel sentence punctuation
 * off the end of a candidate URL, and closing `)]}` only while UNBALANCED
 * against opens inside the URL — `en.wikipedia.org/wiki/Foo_(bar)` keeps its
 * parens, `(see https://x.com)` leaves the `)` outside the anchor.
 */
export function splitTrailingPunctuation(candidate: string): { url: string; trailing: string } {
  let url = candidate;
  let trailing = '';
  for (;;) {
    const last = url.charAt(url.length - 1);
    if (!last) break;
    if (TRAILING_URL_PUNCT.has(last)) {
      url = url.slice(0, -1);
      trailing = last + trailing;
      continue;
    }
    const opener = URL_CLOSER_TO_OPENER[last];
    if (opener) {
      const opens = url.split(opener).length - 1;
      const closes = url.split(last).length - 1;
      if (closes > opens) {
        url = url.slice(0, -1);
        trailing = last + trailing;
        continue;
      }
    }
    break;
  }
  return { url, trailing };
}

/**
 * Minimal HTML escaping for text/attribute content we synthesize. IDEMPOTENT
 * on `&`: an already-encoded entity is left alone (same contract as
 * escapeTableCell) so pre-escaped tile text never renders literal `&amp;amp;`.
 */
export function escapeHtmlText(s: string): string {
  return s
    .replace(/&(?![a-zA-Z#][a-zA-Z0-9]{0,31};)/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Finished external anchor for a bare URL: `www.`-style matches get an
 * https:// scheme prepended on the href, the display keeps the text the
 * author wrote, and target/rel ride on the anchor itself — sanitizeHtmlInput
 * and ensureLinksOpenInNewTab downstream are idempotent over these
 * attributes, never stripping or duplicating them.
 */
export function buildExternalLinkAnchor(url: string): string {
  const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  return `<a href="${escapeHtmlText(href)}" target="_blank" rel="noopener noreferrer">${escapeHtmlText(url)}</a>`;
}

/**
 * Auto-link plain text URLs and wrap them with anchors.
 * Targets http(s) and www. URLs; keeps original text as display.
 * Also converts markdown [label](href) links (F3): internal hrefs become real
 * same-origin anchors (sanitizer re-validates downstream); non-internal ones
 * render as their label only — no link.
 */
/** Markdown pipe-table separator row (`| --- | :---: |`, and GFM's compact
 *  single-dash forms `| - | - |` / `|:-:|:-:|`, which the ≥2-dash pattern used
 *  to reject — leaving those tables as letter-soup text, review 2026-08-03). */
export const PIPE_TABLE_SEPARATOR_RE = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/;

/** IDEMPOTENT escape: an already-encoded entity is left alone, so callers that
 *  pre-escape their input (SpecialMessageTile's text branch) don't render
 *  literal "&amp;amp;" (review 2026-08-03). */
function escapeTableCell(cell: string): string {
  return cell
    .replace(/&(?![a-zA-Z#][a-zA-Z0-9]{0,31};)/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Split on UNESCAPED pipes: `\|` is the markdown escape for a literal pipe in
 *  a cell, and splitting on it shifted the row and dropped the last column's
 *  value entirely (review 2026-08-03). */
export function splitPipeTableRow(line: string): string[] {
  let l = line.trim();
  if (l.startsWith('|')) l = l.slice(1);
  if (/(?<!\\)\|$/.test(l)) l = l.slice(0, -1);
  return l.split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, '|'));
}

/**
 * Markdown pipe tables in plain TEXT → real scroll-wrapped HTML tables
 * (2026-08-02 Miles report: model-written tables rendered as garbled text).
 * Runs inside autoLinkText before the bold pass (cell `**bold**` still converts
 * downstream); the sanitizer admits table tags and the synthesized block is a
 * single line, so the paragraph splitter and `\n→<br>` steps leave it intact.
 * Pure — pinned by chatMarkdownTables.test.tsx.
 */
export function convertPipeTablesInText(text: string): string {
  if (!text || !text.includes('|')) return text;
  const lines = text.split('\n');
  const out: string[] = [];
  let i = 0;
  let inFence = false;
  while (i < lines.length) {
    // Never synthesize a table out of fenced source the user asked to SEE
    // (e.g. "show me the markdown for that table" — review 2026-08-03).
    if (/^\s*(```|~~~)/.test(lines[i])) {
      inFence = !inFence;
      out.push(lines[i]);
      i += 1;
      continue;
    }
    if (inFence) {
      out.push(lines[i]);
      i += 1;
      continue;
    }
    const headerLine = lines[i];
    const sepLine = lines[i + 1];
    if (headerLine && sepLine && headerLine.includes('|') && PIPE_TABLE_SEPARATOR_RE.test(sepLine)) {
      const headers = splitPipeTableRow(headerLine);
      if (headers.length >= 2) {
        const rows: string[][] = [];
        let j = i + 2;
        while (j < lines.length && lines[j].includes('|') && !PIPE_TABLE_SEPARATOR_RE.test(lines[j])) {
          rows.push(splitPipeTableRow(lines[j]));
          j += 1;
        }
        const thead = `<thead><tr>${headers.map((h) => `<th>${escapeTableCell(h)}</th>`).join('')}</tr></thead>`;
        const tbody = rows.length > 0
          ? `<tbody>${rows.map((r) => `<tr>${headers.map((_, c) => `<td>${escapeTableCell(r[c] ?? '')}</td>`).join('')}</tr>`).join('')}</tbody>`
          : '';
        out.push('', `<div class="chat-table-scroll"><table class="markdown-table">${thead}${tbody}</table></div>`, '');
        i = j;
        continue;
      }
    }
    out.push(lines[i]);
    i += 1;
  }
  return out.join('\n');
}

export function autoLinkText(text: string): string {
  if (!text) return '';

  // 0. URL-safe tokenization (link-integrity fix, 2026-08-11): stash inline
  //    code spans, markdown links, and bare URLs behind opaque tokens BEFORE
  //    any formatting pass, so nothing downstream (bold, lists, tables,
  //    paragraph splitting, internal-route autolink) can split or restyle a
  //    URL. Restored as finished HTML in the final step; the downstream
  //    bubble pipeline (normalizeChatHtml → ensureLinksOpenInNewTab)
  //    re-validates every anchor and is idempotent over these attributes.
  const stashed: string[] = [];
  const stash = (html: string): string => {
    stashed.push(html);
    return linkToken(stashed.length - 1);
  };

  // 0a. Inline code spans: display-only — content is never formatted and
  //     never autolinked. Escaped here; the sanitizer admits <code>.
  let result = text.replace(/`([^`\n]+)`/g, (_match, code: string) =>
    stash(`<code>${escapeHtmlText(code)}</code>`));

  // 0b. Markdown [label](href) links — F3 policy unchanged: INTERNAL
  //     whitelist-shaped hrefs become same-tab anchors (now stashed, so a
  //     later pass can never split the href); anything else renders as its
  //     label only — external links still can never enter a bubble through
  //     markdown syntax. A leading '!' (image syntax) is consumed so
  //     `![alt](src)` degrades to its alt text. Label `**bold**` is applied
  //     here because the bold pass no longer sees stashed labels.
  result = result.replace(
    /!?\[([^\]\n]+)\]\(\s*([^()\s]+?)(?:\s+"[^")]*"|\s+'[^')]*')?\s*\)/g,
    (_match, label: string, href: string) => {
      const cleanHref = href.trim();
      if (INTERNAL_ANCHOR_HREF_RE.test(cleanHref)) {
        const boldedLabel = label.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        return stash(`<a href="${cleanHref.replace(/"/g, '&quot;')}">${boldedLabel}</a>`);
      }
      return label;
    },
  );

  // 0c. Bare URLs (https?:// and www.): stashed as finished new-tab anchors;
  //     trailing sentence punctuation and unbalanced )]} stay OUTSIDE the
  //     anchor as plain text (defect: a trailing comma broke the destination).
  result = result.replace(BARE_URL_RE, (match) => {
    const { url, trailing } = splitTrailingPunctuation(match);
    if (!url || !/[a-z0-9]/i.test(url.replace(/^(?:https?:\/\/|www\.)/i, ''))) return match;
    return stash(buildExternalLinkAnchor(url)) + trailing;
  });

  // 1. Headings: ## heading → <strong> block
  result = result.replace(/^#{1,6}\s+(.+)$/gm, '<strong style="display:block;margin:0.5em 0 0.25em;font-size:1.05em">$1</strong>');

  // 1b. Markdown pipe tables → real scroll-wrapped tables (2026-08-02) —
  //     before the bold pass so `**bold**` inside cells still converts.
  result = convertPipeTablesInText(result);

  // 2. Bold: **text** → <strong>text</strong>
  result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // 3. Bullet lists: group consecutive `- item`, `* item`, or `• item` lines.
  //    The Unicode bullet `•` is included so welcome-bubble copy that uses
  //    typographic bullets (e.g. `• Research case law`) renders as a real
  //    `<ul>` with `<li>` items instead of a wall of inline text.
  result = result.replace(/((?:^[ \t]*[-*\u2022][ \t]+.+\n?)+)/gm, (block) => {
    const items = block.trim().split('\n').map(line =>
      `<li>${line.replace(/^[ \t]*[-*\u2022][ \t]+/, '').trim()}</li>`
    ).join('');
    return `<ul style="padding-left:1.5em;list-style-position:outside;margin:0.5em 0">${items}</ul>`;
  });

  // 4. Numbered lists: group consecutive `1. item` lines
  result = result.replace(/((?:^[ \t]*\d+\.[ \t]+.+\n?)+)/gm, (block) => {
    const items = block.trim().split('\n').map(line =>
      `<li>${line.replace(/^[ \t]*\d+\.[ \t]+/, '').trim()}</li>`
    ).join('');
    return `<ol style="padding-left:1.5em;list-style-position:outside;margin:0.5em 0">${items}</ol>`;
  });

  // 4b. The bullet/numbered-list replacements above consume the trailing
  //     newline of the last list line, which can collapse a blank-line
  //     boundary (`\n\n`) between the list and the following paragraph
  //     down to a single `\n`. Re-insert a blank line on each side of
  //     every freshly-synthesised list block so the paragraph splitter
  //     in step 6 still sees the boundary it needs.
  result = result.replace(/(<\/(?:ul|ol)>)/g, '\n\n$1\n\n').replace(/(<(?:ul|ol)\b)/g, '\n\n$1');

  // (Steps 4c/5 — markdown links and bare-URL anchors — moved to steps 0b/0c
  //  above as part of the 2026-08-11 link-integrity tokenization.)

  // 5b. Whitelisted internal routes (L3, 2026-07-15): plain-text mentions of
  //     /auth, /pricing, … in guest replies / notice bubbles become
  //     same-origin anchors so the sign-in invitation is actually clickable.
  //     Boundary-guarded on both sides; absolute URLs (e.g.
  //     https://prest0.ai/auth) were already stashed as opaque tokens in
  //     step 0c, so a path inside one can never be re-matched into a nested
  //     anchor.
  const internalAlternation = INTERNAL_AUTOLINK_ROUTES
    .map((route) => route.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');
  const internalRouteRegex = new RegExp(
    `(^|[\\s(])(${internalAlternation})(?=$|[\\s).,;:!?…—])`,
    'gm',
  );
  result = result.replace(internalRouteRegex, (_match, prefix, route) =>
    `${prefix}<a href="${route}">${route}</a>`);

  // 6. Paragraph breaks: a blank line (`\n\n` or more) splits surrounding
  //    plain-text into real `<p>` blocks so the chat-bubble CSS that styles
  //    `p { margin: 0 0 0.75em 0 }` produces visible vertical separation
  //    between paragraphs (e.g. between the intro line and the bullet list).
  //    Pieces that are already a block element (<ul>/<ol>/<p>/<div>/<h*>)
  //    are left as-is and not re-wrapped in a `<p>` — putting a block
  //    element inside a `<p>` is invalid HTML and the browser would close
  //    the `<p>` early, eating the visual paragraph break.
  const BLOCK_OPEN_RE = /<(ul|ol|p|div|h[1-6]|blockquote|pre|table|hr)\b[^>]*>/i;
  const STARTS_WITH_BLOCK_RE = /^<(ul|ol|p|div|h[1-6]|blockquote|pre|table|hr)\b/i;
  const BLOCK_PAIR_RE = /<(ul|ol|p|div|h[1-6]|blockquote|pre|table)\b[^>]*>[\s\S]*?<\/\1>/i;

  /**
   * Split a single inter-blank-line segment further so any embedded
   * block element (e.g. a `<ul>` we just synthesised for bullets) is
   * lifted out of its surrounding plain-text — the plain-text parts
   * each become their own `<p>`, the block stays as-is.
   */
  const splitSegmentAroundBlocks = (seg: string): string[] => {
    if (!BLOCK_OPEN_RE.test(seg)) return [seg];
    const parts: string[] = [];
    let rest = seg;
    // Repeatedly peel off the next block element along with any plain
    // text that precedes it.
    // We tolerate either a complete `<tag>...</tag>` pair (preferred,
    // since we know we just synthesised them) or a void tag like `<hr>`.
    while (rest.length > 0) {
      const pairMatch = rest.match(BLOCK_PAIR_RE);
      if (!pairMatch || pairMatch.index === undefined) {
        parts.push(rest);
        break;
      }
      const before = rest.slice(0, pairMatch.index);
      const block = pairMatch[0];
      if (before) parts.push(before);
      parts.push(block);
      rest = rest.slice(pairMatch.index + block.length);
    }
    return parts;
  };

  const segments = result.split(/\n{2,}/).flatMap(splitSegmentAroundBlocks);
  result = segments
    .map(seg => {
      // Strip leading/trailing newlines but keep internal `\n` so step 7
      // can convert them into `<br>` for intra-paragraph soft breaks.
      const trimmed = seg.replace(/^\s+|\s+$/g, '');
      if (!trimmed) return '';
      // Don't double-wrap content that is already a block-level element
      // (e.g. the `<ul>` we just produced for bullets).
      if (STARTS_WITH_BLOCK_RE.test(trimmed)) return trimmed;
      return `<p>${trimmed}</p>`;
    })
    .filter(Boolean)
    .join('');

  // 7. Single newlines that survive inside paragraphs/list items become <br>
  //    (intra-paragraph soft line breaks). Newlines inside a freshly-built
  //    block element (e.g. between `<li>` items) are not re-broken.
  result = result.replace(/\n/g, '<br>');

  // 8. Restore the code spans / anchors stashed in step 0 as finished HTML.
  //    Looped (bounded) because a stashed fragment can itself embed a token —
  //    e.g. a markdown link whose label contained a code span stashed in 0a —
  //    and String.replace never rescans replacement text (review F7). An
  //    unknown/forged token id restores to ''.
  for (let pass = 0; pass < 5 && LINK_TOKEN_RE.test(result); pass += 1) {
    LINK_TOKEN_RE.lastIndex = 0;
    result = result.replace(LINK_TOKEN_RE, (_match, i: string) => stashed[Number(i)] ?? '');
  }
  LINK_TOKEN_RE.lastIndex = 0;

  return result;
}

// ============================================================================
// HTML to Plain Text Conversion
// ============================================================================

/**
 * Block-level HTML tags that should produce newlines.
 */
const BLOCK_TAGS = ['P', 'DIV', 'BLOCKQUOTE', 'PRE', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'CAPTION'];

/** A table's structural tags: walked for their rows only (see htmlToPlainText). */
const TABLE_SECTION_TAGS = ['TABLE', 'THEAD', 'TBODY', 'TFOOT'];

/**
 * Convert HTML content to plain text, preserving basic structure.
 * Handles lists, line breaks, and block elements appropriately.
 * 
 * @param html - The HTML string to convert
 * @returns Plain text representation of the HTML
 */
export function htmlToPlainText(html: string): string {
  if (!html) return '';

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    let lines: string[] = [];

    const walk = (node: Node): void => {
      if (node.nodeType === Node.TEXT_NODE) {
        lines.push(node.textContent || '');
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      
      const el = node as HTMLElement;

      // A TABLE copies as rows of tab-separated cells (2026-09-22), so it
      // pastes into a spreadsheet as the grid it was — it used to run every
      // cell of a row into one word. A cell's own line breaks become spaces:
      // a newline inside a cell would start a new row wherever it is pasted.
      if (el.tagName === 'TR') {
        const prev = lines.length ? lines[lines.length - 1] : '';
        if (lines.length && !prev.endsWith('\n')) lines.push('\n');
        const cells = Array.from(el.children).filter((c) => c.tagName === 'TD' || c.tagName === 'TH');
        const outer = lines;
        cells.forEach((cell, i) => {
          lines = [];
          Array.from(cell.childNodes).forEach(walk);
          const text = lines.join('').replace(/\s*\n\s*/g, ' ').replace(/\t/g, ' ').trim();
          lines = outer;
          if (i > 0) lines.push('\t');
          lines.push(text);
        });
        lines.push('\n');
        return;
      }
      if (TABLE_SECTION_TAGS.includes(el.tagName)) {
        // Only the rows: the whitespace between a table's tags is layout.
        const prev = lines.length ? lines[lines.length - 1] : '';
        if (el.tagName === 'TABLE' && lines.length && !prev.endsWith('\n')) lines.push('\n');
        Array.from(el.children).forEach(walk);
        if (el.tagName === 'TABLE') lines.push('\n');
        return;
      }

      // A COMPOSER MENTION CHIP contributes its handle and nothing else
      // (2026-09-22). The chip shows the person's full name, and the send
      // path reads this function — so without this branch a draft reading
      // "Malaika Majeed" would transmit that name, the mention grammar would
      // match nothing, and SHE WOULD SILENTLY NOT BE NOTIFIED.
      //
      // This is deliberately THE one door rather than a second reader threaded
      // through the composer's call sites: every existing reader of the draft
      // (the send path, both draft-persistence writes, the paste lane, the
      // chip/undo path) already passes through here, and so does every reader
      // added later by someone who has never read this file.
      //
      // It cannot touch a TRANSCRIPT chip: that one carries `mention-chip`
      // alone, and `isComposerChip` additionally requires the `mention-h-`
      // class that only `composerChipHtml` mints — so a rendered message still
      // reads as the person's NAME, which is what its reader wants. A PASTED
      // span cannot forge one either: `sanitizePastedHtml` strips every class
      // unconditionally.
      //
      // Spaced where it sits (chipTextInContext): a chip the words around it
      // came to touch still sends as a mention of that person.
      if (isComposerChip(el)) {
        const text = chipTextInContext(el, doc.body);
        if (text) {
          lines.push(text);
          return;
        }
      }

      // A TYPESET FORMULA is read as its text rendering (2026-09-23): the
      // span keeps it, visually hidden, beside KaTeX's copy, whose own text is
      // glyphs in layout order ("a+bc" for (a+b)/c). The copy button,
      // read-aloud and a reply quote all read through here.
      if (el.classList.contains('katex-host')) return;

      const listItem = el.tagName === 'LI';
      const lineBreak = el.tagName === 'BR';

      if (lineBreak) {
        lines.push('\n');
        return;
      }
      const isBlock = BLOCK_TAGS.includes(el.tagName);
      // A block starts on its own line (owner report 2026-09-07: items typed
      // on separate lines ran together). Chrome/Safari represent every typed
      // line after the first as its own <div> — `milk<div>eggs</div>` — so a
      // block that follows inline text needs a LEADING break too, not only
      // the trailing one below. Adjacent blocks (`</div><div>`) already end
      // in '\n', so this never double-breaks them.
      if (isBlock || listItem) {
        const prev = lines.length ? lines[lines.length - 1] : '';
        if (lines.length && !prev.endsWith('\n')) lines.push('\n');
      }
      if (listItem) {
        // A numbered list keeps its numbers (one draft bar step 12): the words a
        // person sends are the words they wrote, and the marked words a teammate
        // reads (markedWords.ts) number the same items the same way.
        const list = el.parentElement;
        if (list && list.tagName === 'OL') {
          const start = Number(list.getAttribute('start')) || 1;
          const index = Array.from(list.children).filter((c) => c.tagName === 'LI').indexOf(el);
          lines.push(`${start + Math.max(index, 0)}. `);
        } else {
          lines.push('- ');
        }
      }

      Array.from(el.childNodes).forEach(walk);

      if (isBlock || listItem) {
        lines.push('\n');
      }
    };

    Array.from(doc.body.childNodes).forEach(walk);
    const combined = lines.join('').replace(/\n{3,}/g, '\n\n').trim();
    return combined;
  } catch {
    // Fallback: simple tag stripping
    return html.replace(/<\/?[^>]+(>|$)/g, '');
  }
}

/** Plain-text renderings kept, and the characters of keys and values together. */
export const PLAIN_TEXT_CACHE_MAX_ENTRIES = 1_500;
export const PLAIN_TEXT_CACHE_MAX_CHARS = 4_000_000;

const plainTextCache = new Map<string, string>();
let plainTextCacheChars = 0;

/**
 * htmlToPlainText for a MESSAGE BODY, remembered (2026-09-28, Malaika's
 * report: typing stalled while a reply streamed in). Every row of the
 * transcript asks for its plain text on every render, to decide whether it
 * shows "read aloud", and every render of a streaming reply renders every row:
 * a full HTML parse per row per frame, about 8 ms for a 373-message thread on
 * a fast machine. The conversion depends on the html alone, so a body already
 * converted is a lookup; the rendered-body memo hands back the same string for
 * a settled row, so the lookup does not even re-hash it.
 *
 * Least recently used, bounded by entries and by the characters of keys and
 * values together (a key is a body's html). The draft box never comes here:
 * it reads htmlToPlainText directly, once per send.
 */
export function htmlToPlainTextCached(html: string): string {
  if (!html) return '';
  const hit = plainTextCache.get(html);
  if (hit !== undefined) {
    plainTextCache.delete(html);
    plainTextCache.set(html, hit);
    return hit;
  }
  const text = htmlToPlainText(html);
  const size = html.length + text.length;
  if (size > PLAIN_TEXT_CACHE_MAX_CHARS) return text;
  plainTextCache.set(html, text);
  plainTextCacheChars += size;
  while (
    (plainTextCache.size > PLAIN_TEXT_CACHE_MAX_ENTRIES || plainTextCacheChars > PLAIN_TEXT_CACHE_MAX_CHARS)
    && plainTextCache.size > 1
  ) {
    const oldest = plainTextCache.keys().next().value as string;
    plainTextCacheChars -= oldest.length + (plainTextCache.get(oldest) || '').length;
    plainTextCache.delete(oldest);
  }
  return text;
}

/** Tests only. */
export function resetPlainTextCacheForTests(): void {
  plainTextCache.clear();
  plainTextCacheChars = 0;
}

// ============================================================================
// File Utilities
// ============================================================================

/**
 * Format a file size in bytes to a human-readable string.
 * 
 * @param bytes - The file size in bytes
 * @returns Formatted string (e.g., "1.5MB", "256KB")
 */
export function formatFileSize(bytes: number): string {
  const KB = 1024;
  const MB = KB * 1024;
  const GB = MB * 1024;

  if (bytes < KB) {
    return bytes + 'B';
  } else if (bytes < MB) {
    return Math.floor(bytes / KB) + 'KB';
  } else if (bytes < GB) {
    return (bytes / MB).toFixed(1) + 'MB';
  } else {
    return (bytes / GB).toFixed(1) + 'GB';
  }
}

/**
 * A recording's length as a player shows it: "0:42", "11:04", "1:02:03"
 * (2026-10-08). Empty for anything that is not a finite, positive length.
 */
export function formatMediaLength(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/**
 * A size LIMIT as a person would write it: "50GB", "300MB", "1.5GB".
 *
 * The oversize notice used to print `Math.round(limit / MiB) + 'MB'`, which was
 * fine at 25MB and became "1024MB" at a gigabyte and "51200MB" at the 50 GB
 * limit (2026-10-08). A limit is a round number, so a trailing ".0" is noise.
 */
export function formatSizeLimit(bytes: number): string {
  return formatFileSize(bytes).replace(/\.0(?=[A-Z])/, '');
}

/**
 * Truncate a filename to a maximum length with TRUE MIDDLE truncation,
 * preserving the extension (2026-08-11: the old head-truncation dropped the
 * distinctive tail — generated deliverables carry their date stamp at the END,
 * so `listing-brokerage-…-2026-08-11.pdf` became `listing-brokerage-r....pdf`).
 *
 * A head chunk and a tail chunk of the basename are joined by a single '…';
 * the tail keeps roughly the last 10-12 characters of the basename (usually
 * the date stamp). Total output length is always ≤ maxLength.
 *
 * Edge cases: names within maxLength return unchanged; dotfiles (`.env…`) and
 * extensionless names middle-truncate as a whole; multi-dot names treat only
 * the LAST segment as the extension.
 *
 * @param name - The filename to truncate
 * @param maxLength - Maximum length (default: 24)
 * @returns Truncated filename with a middle ellipsis if needed
 */
export function truncateFilename(name: string, maxLength: number = 24): string {
  if (name.length <= maxLength) return name;

  // Extension = last '.' segment. A leading dot (dotfile) is part of the
  // basename, not an extension marker, and a "last segment" longer than any
  // real extension (>10 chars after the dot) is treated as part of the name.
  const lastDot = name.lastIndexOf('.');
  const hasExtension =
    lastDot > 0 && lastDot < name.length - 1 && name.length - lastDot <= 11;
  const extension = hasExtension ? name.slice(lastDot) : ''; // includes the '.'
  const base = hasExtension ? name.slice(0, lastDot) : name;

  // Budget for head + tail of the basename ('…' costs 1, extension rides free).
  const budget = maxLength - extension.length - 1;
  if (budget < 3) {
    // Degenerate: maxLength can't fit a meaningful head…tail + extension
    // (e.g. a huge extension). Hard-cut the whole name instead.
    return `${name.slice(0, Math.max(1, maxLength - 1))}…`;
  }

  const tailLength = Math.min(11, base.length - 1, Math.max(1, Math.floor(budget / 2)));
  const headLength = budget - tailLength;
  return `${base.slice(0, headLength)}…${base.slice(base.length - tailLength)}${extension}`;
}

/**
 * Validate file size against maximum allowed.
 * 
 * @param file - The file to validate
 * @param maxSize - Maximum size in bytes
 * @returns True if file is within size limit
 */
export function isFileSizeValid(file: File, maxSize: number): boolean {
  return file.size <= maxSize;
}

/**
 * Separate valid and oversized files from a FileList.
 * 
 * @param files - The files to validate
 * @param maxSize - Maximum size in bytes
 * @returns Object with arrays of valid and oversized files
 */
export function validateFiles(
  files: FileList | File[],
  maxSize: number
): { validFiles: File[]; oversizedFiles: string[] } {
  const validFiles: File[] = [];
  const oversizedFiles: string[] = [];

  Array.from(files).forEach(file => {
    if (file.size <= maxSize) {
      validFiles.push(file);
    } else {
      oversizedFiles.push(file.name);
    }
  });

  return { validFiles, oversizedFiles };
}

// ============================================================================
// File Type Detection
// ============================================================================

/**
 * Determine the category of a file based on its MIME type.
 */
export type FileCategory = 'image' | 'pdf' | 'document' | 'spreadsheet' | 'presentation' | 'html' | 'generic';

/**
 * Get the category of a file based on its MIME type.
 * 
 * @param fileType - The MIME type of the file
 * @returns The file category
 */
export function getFileCategory(fileType: string | undefined): FileCategory {
  if (!fileType) return 'generic';

  if (fileType.includes('image')) return 'image';
  if (fileType.includes('pdf')) return 'pdf';
  if (fileType.includes('word') || fileType.includes('doc')) return 'document';
  if (fileType.includes('sheet') || fileType.includes('excel') || fileType.includes('csv')) return 'spreadsheet';
  if (fileType.includes('presentation') || fileType.includes('powerpoint') || fileType.includes('ppt')) return 'presentation';
  // Interactive HTML deliverables (tier-21 3D scenes ship as text/html — 2026-08-01).
  if (fileType.includes('html')) return 'html';

  return 'generic';
}

// ============================================================================
// Time Formatting
// ============================================================================

/**
 * Format a timestamp for display in chat messages.
 *
 * @param timestamp - The date to format
 * @returns Formatted time string (e.g., "2:30 PM")
 */
export function formatMessageTime(timestamp: Date | string): string {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  // Seconds always (Miles, 2026-09-09: "the timestamp easy to understand down
  // to the second, not minutes").
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return `${month}/${day}/${year} ${time}`;
}

/**
 * Format AI model processing time (responseTimeMs) for display.
 * Converts milliseconds to a human-readable format with appropriate units.
 *
 * @param ms - The processing time in milliseconds
 * @returns Formatted time string (e.g., "1.2s", "2m 30s", "1h 5m 30s")
 */
export function formatProcessingTime(ms?: number): string | null {
  if (!ms || ms <= 0) return null;

  if (ms < 1000) {
    return `${ms}ms`;
  }

  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) {
    // Show one decimal for short durations, whole seconds otherwise
    return ms < 10000 ? `${(ms / 1000).toFixed(1)}s` : `${totalSeconds}s`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) {
    return `${minutes}m ${seconds}s`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m ${seconds}s`;
}

// ============================================================================
// Email Subscription (for guest email collection)
// ============================================================================

export interface EmailSubscription {
  email: string;
  timestamp: string;
  source: 'chat' | 'signup';
  metadata?: Record<string, unknown>;
}

/**
 * Generate a unique 9-digit ID.
 * 
 * @param existingIds - Set of IDs to check against for uniqueness
 * @returns A unique 9-digit string
 */
function generateUniqueId(existingIds: Set<string>): string {
  let id = '';
  do {
    const firstDigit = Math.floor(Math.random() * 9) + 1; // 1-9
    const remainingDigits = Array.from({ length: 8 }, () => Math.floor(Math.random() * 10)).join('');
    id = `${firstDigit}${remainingDigits}`;
  } while (existingIds.has(id));
  return id;
}

/**
 * Save an email subscription to localStorage for guest tracking.
 * 
 * @param email - The email address to save
 * @param source - Where the subscription came from
 * @param metadata - Optional additional data
 * @returns True if the email was newly saved, false if it already existed
 */
export function saveEmailSubscription(
  email: string,
  source: 'chat' | 'signup',
  metadata?: Record<string, unknown>
): boolean {
  try {
    // Get existing data from all storage keys
    const existingEmailSubsStr = localStorage.getItem('email_subscriptions');
    const existingUserAccountsStr = localStorage.getItem('user_accounts');

    const existingEmailSubs: EmailSubscription[] = existingEmailSubsStr ? JSON.parse(existingEmailSubsStr) : [];
    const existingUserAccounts: EmailSubscription[] = existingUserAccountsStr ? JSON.parse(existingUserAccountsStr) : [];

    // Combine both account sources into a map to remove duplicates
    const allAccountsMap = new Map<string, EmailSubscription>();
    [...existingEmailSubs, ...existingUserAccounts].forEach(item => {
      allAccountsMap.set(item.email.toLowerCase(), item);
    });

    // Convert back to array
    const existingData = Array.from(allAccountsMap.values());

    // Check if email already exists
    const emailExists = existingData.some(item => item.email.toLowerCase() === email.toLowerCase());

    if (!emailExists) {
      // Collect all existing IDs
      const existingAccountIds = new Set<string>();
      const existingMemberIds = new Set<string>();

      existingData.forEach(item => {
        if (item.metadata?.accountId) existingAccountIds.add(String(item.metadata.accountId));
        if (item.metadata?.profileId) existingMemberIds.add(String(item.metadata.profileId));
      });

      // Generate unique IDs
      const accountId = generateUniqueId(existingAccountIds);
      const memberId = generateUniqueId(new Set([...existingMemberIds, accountId]));

      // Create new subscription
      const newSubscription: EmailSubscription = {
        email,
        timestamp: new Date().toISOString(),
        source,
        metadata: {
          ...(metadata || {}),
          accountId,
          profileId: memberId,
        },
      };

      const updatedData = [...existingData, newSubscription];

      // Save to both storage keys for compatibility
      localStorage.setItem('email_subscriptions', JSON.stringify(updatedData));
      localStorage.setItem('user_accounts', JSON.stringify(updatedData));

      // Trigger a storage event to notify other components
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'user_accounts',
        newValue: JSON.stringify(updatedData),
      }));

      return true;
    }

    return false;
  } catch (error) {
    console.error('Error saving email:', error);
    return false;
  }
}

// ============================================================================
// Local Storage Utilities
// ============================================================================

/**
 * Safely get a value from localStorage.
 *
 * THIS TAB'S LATEST COPY WINS OVER A FROZEN ONE (2026-09-23): when the storage
 * bin was full, a write of this key was refused and the copy on disk is older
 * than what this tab holds — the held copy (threadCacheTier) is returned
 * instead, so no reader acts on the frozen list as if it were current. Absent a
 * held copy this is the plain localStorage read it always was.
 *
 * @param key - The storage key
 * @param defaultValue - Default value if key doesn't exist
 * @returns The stored value or default
 */
export function getFromLocalStorage<T>(key: string, defaultValue: T): T {
  // A save held back for this key runs first (threadCacheTier, 2026-09-28):
  // no reader ever sees an older copy than it would have.
  flushDeferredThreadSave(key);
  try {
    const stored = readHeldCopy(key) ?? localStorage.getItem(key);
    if (stored === null) return defaultValue;
    return JSON.parse(stored) as T;
  } catch {
    return defaultValue;
  }
}

/**
 * Is there a value stored under this key AT ALL?
 *
 * getFromLocalStorage cannot answer that: it returns the caller's default for
 * "no such key" and for "key holds an empty value" alike. For most callers the
 * distinction does not matter. For anything that treats an empty read as
 * EVIDENCE it matters completely — absence of a transcript is not evidence
 * that a conversation is empty, and reading it that way is how real threads
 * got stamped {messageCount: 0, hasUserContent: false} and archived as blank
 * drafts on any device that had never stored them (incognito, a second
 * browser, a quota eviction).
 *
 * Returns false when storage itself is unavailable, so a caller that guards
 * on this degrades to "do not touch the record" rather than to a destructive
 * default.
 */
export function localStorageHasKey(key: string): boolean {
  flushDeferredThreadSave(key);
  // A held copy is a value this tab stored that the full bin refused (see
  // getFromLocalStorage) — the key has a value.
  if (readHeldCopy(key) !== null) return true;
  try {
    return localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

/**
 * Safely set a value in localStorage.
 *
 * QUOTA SELF-HEAL (2026-08-22, b815 incident): a full origin bin used to make
 * this swallow QuotaExceededError forever — every save silently vanished, so
 * reads returned a FROZEN stale state (stuck "Queued" behind a dead turn,
 * transcripts "going missing", and the Firestore SDK's own writes crashing
 * the page). Writes now route through the quota manager: on quota exhaustion
 * it evicts cold per-thread caches (server store is the durable copy) and
 * retries, and only degrades — loudly, once — when even eviction can't help.
 *
 * @param key - The storage key
 * @param value - The value to store
 */
export function setToLocalStorage<T>(key: string, value: T): void {
  // Writes keep their order: a save held back for this key lands first.
  flushDeferredThreadSave(key);
  try {
    quotaSafeSetItem(key, JSON.stringify(value));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

/**
 * Safely remove a value from localStorage.
 * 
 * @param key - The storage key to remove
 */
export function removeFromLocalStorage(key: string): void {
  // A removed key has nothing for a held copy to stand in for, and a save
  // held back for it is cancelled (releaseHeldCopy): it must not come back.
  releaseHeldCopy(key);
  try {
    localStorage.removeItem(key);
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

// ============================================================================
// Event Utilities
// ============================================================================

/**
 * Custom event names used by the chat system.
 */
export const CHAT_EVENTS = {
  USER_MESSAGE: 'chat:user-message',
  ADD_PLATFORM_MESSAGES: 'chat:add-platform-messages',
  ADD_SYSTEM_MESSAGE: 'chat:add-system-message',
} as const;

/**
 * Dispatch a chat event with the given detail.
 * 
 * @param eventName - The event name to dispatch
 * @param detail - The event detail payload
 */
export function dispatchChatEvent<T>(eventName: string, detail: T): void {
  try {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  } catch {
    // Silently fail
  }
}

// ============================================================================
// Special Message Builder Utilities
// ============================================================================

/**
 * Consistent color palette for special status messages.
 * All deep research, formatting, and status messages use these colors.
 */
export const STATUS_MESSAGE_COLORS = {
  // Prest0 brand scheme (2026-08-12) — mirrors SPECIAL_MESSAGE_COLORS in
  // specialMessageColors.ts: warm ink + gold, distinction by contrast, never a one-off hue.
  /** Primary accent for titles (warm ink) */
  accent: '#1C1917',
  /** Light background for icon badges (gold tint) */
  accentBg: 'rgba(198, 167, 94, 0.14)',
  /** Border accent color */
  border: '#E0D5BC',
  /** Subtitle/body text */
  subtitle: '#6B6459',
  /** Muted helper text */
  muted: 'var(--brand-text-muted)',
} as const;

/**
 * Configuration for building a status message.
 */
export interface StatusMessageConfig {
  /** Icon to display (emoji or symbol) */
  icon?: string;
  /** Main title text */
  title: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Optional helper text (smaller, muted) */
  helperText?: string;
  /** Whether to show left border accent (default: true) */
  showBorder?: boolean;
}

/**
 * Build consistent HTML for status messages used in deep research and formatting flows.
 * Uses the unified dark blue color scheme for visual consistency.
 * 
 * @param config - Status message configuration
 * @returns HTML string for the status message
 */
export function buildStatusMessageHtml(config: StatusMessageConfig): string {
  const { icon, title, subtitle, helperText, showBorder = true } = config;
  const c = STATUS_MESSAGE_COLORS;

  const borderStyle = showBorder
    ? `padding-left:12px;border-left:3px solid ${c.border};margin:4px 0;`
    : '';

  const iconHtml = icon
    ? `<span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;background:${c.accentBg};color:${c.accent};font-weight:700;font-size:0.85rem;flex-shrink:0;">${icon}</span>`
    : '';

  const titleHtml = `<div style="display:flex;align-items:center;gap:8px;">
    ${iconHtml}
    <strong style="color:${c.accent};">${title}</strong>
  </div>`;

  const subtitleHtml = subtitle
    ? `<span style="color:${c.subtitle};font-size:0.9rem;line-height:1.5;">${subtitle}</span>`
    : '';

  const helperHtml = helperText
    ? `<p style="margin:8px 0 0 0;color:${c.muted};font-size:0.8rem;line-height:1.4;">${helperText}</p>`
    : '';

  return `<div style="display:flex;flex-direction:column;gap:8px;${borderStyle}">
    ${titleHtml}
    ${subtitleHtml}
    ${helperHtml}
  </div>`;
}

// ============================================================================
// Chat History Utilities (for Orchestrator)
// ============================================================================

/**
 * ChatMessage interface (minimal - for import-free usage)
 */
interface ChatMessage {
  text: string;
  isUser: boolean;
  timestamp: Date | string;
  attachments?: File[];
  attachmentsSerialized?: { name: string; type: string; size: number; data: string }[];
  html?: string;
  sourceTag?: string;
}

/**
 * Get last N messages from localStorage chat history.
 * Used by orchestrator to analyze conversation context.
 *
 * @param storageKey - The localStorage key for chat history
 * @param count - Number of messages to retrieve (default: 20)
 * @returns Array of ChatMessage objects
 */
export function getLastNMessages(
  storageKey: string,
  count: number = 20
): ChatMessage[] {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];

    // Deserialize messages (convert ISO strings back to Dates)
    const messages: ChatMessage[] = parsed.map(msg => ({
      ...msg,
      timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
      attachments: undefined, // Don't reload File objects (avoid memory issues)
      attachmentsSerialized: msg.attachmentsSerialized
    }));

    // Return last N messages
    return messages.slice(-count);
  } catch (err) {
    console.error('[getLastNMessages] Failed to load history:', err);
    return [];
  }
}
